import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, Stat } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, PencilSimple, Trash, IdentificationCard, Buildings } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/currency";

export default function EmployeesPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", surname: "", department: "", email: "" });
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [prefix, setPrefix] = useState("EMP");

  const load = async () => {
    if (!activeCompany) return;
    const { data } = await api.get(`/companies/${activeCompany.id}/employees`);
    setRows(data);
    setPrefix(activeCompany.employee_prefix || "EMP");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompany?.id]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", surname: "", department: "", email: "" });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || "", surname: e.surname || "",
      department: e.department || "", email: e.email || "",
    });
    setOpen(true);
  };

  const save = async (ev) => {
    ev.preventDefault();
    try {
      if (editing) {
        await api.patch(`/companies/${activeCompany.id}/employees/${editing.id}`, form);
        toast.success("Employee updated");
      } else {
        const { data } = await api.post(`/companies/${activeCompany.id}/employees`, form);
        toast.success(`Employee ${data.employee_number} created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Remove ${e.name} ${e.surname}? Employees with claims are deactivated instead.`)) return;
    try {
      const { data } = await api.delete(`/companies/${activeCompany.id}/employees/${e.id}`);
      toast.success(data.deactivated ? "Deactivated (has claim history)" : "Deleted");
      load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || e2.message); }
  };

  const savePrefix = async () => {
    try {
      await api.patch(`/companies/${activeCompany.id}`, {
        employee_prefix: prefix.trim().toUpperCase() || null,
      });
      toast.success(`Prefix set to ${prefix.toUpperCase()}. New employees will use this.`);
      setPrefixOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  const activeCount = rows.filter((r) => r.active).length;
  const inactiveCount = rows.length - activeCount;
  const totalOutstanding = rows.reduce((s, r) => s + (r.outstanding_base || 0), 0);

  return (
    <>
      <PageHeader
        eyebrow="Expenses"
        title="Employees"
        description={`Manage staff eligible to submit expense claims. Employee numbers auto-generate with your company prefix (${prefix}-001, ${prefix}-002 …).`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPrefixOpen(true)} data-testid="emp-prefix-btn" className="rounded-md">
              <IdentificationCard size={14} className="mr-2" /> Prefix
            </Button>
            <Button size="sm" onClick={openCreate} data-testid="emp-new-btn" className="rounded-md">
              <UserPlus size={14} className="mr-2" /> New Employee
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Active employees" value={activeCount} icon={Buildings} testId="stat-emp-active" />
          <Stat label="Inactive" value={inactiveCount} testId="stat-emp-inactive" />
          <Stat label={`Outstanding (${activeCompany.currency})`} value={fmtMoney(totalOutstanding, activeCompany.currency)} tone="negative" testId="stat-emp-outstanding" />
        </div>

        <div className="qb-card overflow-hidden">
          <table className="w-full qb-table" data-testid="employees-table">
            <thead>
              <tr>
                <th>Employee #</th>
                <th>Name</th>
                <th>Department</th>
                <th>Email</th>
                <th className="text-right">Outstanding</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`}>
                  <td className="num font-medium">{e.employee_number}</td>
                  <td>{e.name} {e.surname}</td>
                  <td className="text-muted-foreground">{e.department || "—"}</td>
                  <td className="text-muted-foreground">{e.email || "—"}</td>
                  <td className="text-right num">{fmtMoney(e.outstanding_base || 0, activeCompany.currency)}</td>
                  <td>
                    <span className={`tag-badge ${e.active ? "text-primary" : "text-muted-foreground"}`}>
                      {e.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(e)} className="rounded-md h-8" data-testid={`emp-edit-${e.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(e)} className="rounded-md h-8 text-destructive" data-testid={`emp-del-${e.id}`}>
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No employees yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{editing ? `Edit ${editing.employee_number}` : "New Employee"}</DialogTitle>
            {!editing && <DialogDescription>Employee number will be auto-generated: <b>{prefix}-{String((rows.at(-1)?.employee_seq || 0) + 1).padStart(3, "0")}</b></DialogDescription>}
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">First name</Label>
                <Input required data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-md" />
              </div>
              <div>
                <Label className="text-xs font-medium">Surname</Label>
                <Input required data-testid="emp-surname" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className="mt-1 rounded-md" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Department</Label>
              <Input data-testid="emp-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1 rounded-md" placeholder="e.g. Finance, Sales, Ops" />
            </div>
            <div>
              <Label className="text-xs font-medium">Email (optional)</Label>
              <Input type="email" data-testid="emp-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-md" />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="emp-save" className="rounded-md">{editing ? "Save" : "Create Employee"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Prefix editor */}
      <Dialog open={prefixOpen} onOpenChange={setPrefixOpen}>
        <DialogContent className="rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Employee number prefix</DialogTitle>
            <DialogDescription>Applied to newly-created employees only. Existing numbers stay unchanged.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-medium">Prefix</Label>
            <Input maxLength={8} value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} data-testid="emp-prefix-input" className="mt-1 rounded-md num" />
            <div className="text-xs text-muted-foreground mt-2">Next employee will be <b>{prefix.toUpperCase() || "EMP"}-{String((rows.at(-1)?.employee_seq || 0) + 1).padStart(3, "0")}</b></div>
          </div>
          <DialogFooter>
            <Button onClick={savePrefix} data-testid="emp-prefix-save" className="rounded-md">Save Prefix</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
